import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import {useAuth} from '@/products/auth/auth-provider'
import type {Block} from '@/services/kontent-ai/types'
import {KontentResponsivePicture} from '@/products/flexible-web-toolkit/components/responsive-picture'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'

export type HeroSectionContentItem = IContentItem<{
	title: Elements.TextElement
	subtitle: Elements.RichTextElement
	image: Elements.AssetsElement
	mobileImage: Elements.AssetsElement
	backgroundImage: Elements.MultipleChoiceElement
	imageLeftOrRight: Elements.MultipleChoiceElement
}>

export const HeroSectionBlock: Block<HeroSectionContentItem> = ({
	block: {
		elements: {imageLeftOrRight, image, mobileImage, subtitle, title},
	},
	...context
}) => {
	const authState = useAuth()

	const reversed = imageLeftOrRight[0]?.codename === 'left'

	const heroImage = (
		<KontentResponsivePicture
			className="max-h-[285px] w-full object-contain md:max-h-[380px]"
			desktopAsset={image[0] ?? mobileImage[0]}
			fallbackHeight={380}
			fallbackWidth={380}
			fetchPriority="high"
			mobileAsset={mobileImage[0]}
			priority
			quality={65}
			sizes="(max-width: 768px) 100vw, 50vw"
		/>
	)

	return (
		<div
			className={clsx(
				`flex`,
				reversed
					? 'flex-col-reverse md:flex-row-reverse'
					: 'flex-col md:flex-row',
				`container-wide relative z-[-1] py-4 md:py-0 lg:-mb-10 lg:-mt-16`
			)}
		>
			<div
				className={clsx(
					`flex`,
					reversed ? 'md:justify-end' : null,
					`md:basis-1/2 md:items-center md:justify-center`
				)}
			>
				<div
					className={clsx(
						`w-full py-4 md:max-w-md`,
						reversed ? 'md:px-4' : null
					)}
				>
					{title ? (
						<h1 className="mb-2 text-4xl font-semibold text-midnight-blue">
							{title.replaceAll(
								/{{petOwner}}/gi,
								authState.userDetails.firstName
							)}
						</h1>
					) : null}
					<RichTextRenderer
						className="text-lg text-charcoal lg:text-xl"
						element={subtitle}
						{...context}
					/>
				</div>
			</div>
			<div
				className={clsx(
					reversed ? 'justify-end' : null,
					`md:basis-1/2`
				)}
			>
				{heroImage}
			</div>
		</div>
	)
}
